import {
  ErrorProduct,
  ErrorProductType,
  ErrorUploadProduct,
  ProductCreation,
  ProductCreationWithErrors,
  ProductRaw,
} from '@Arti-zen/package-backoffice'
import { ApiArtizenBO } from 'utils/artizenConnector'

const errorProperties: Record<ErrorProductType, (keyof ProductCreation)[]> = {
  [ErrorProductType.NO_DESCRIPTION]: ['description'],
  [ErrorProductType.NO_PRICE]: ['price'],
  [ErrorProductType.NAN_NETPRICE]: ['netPrice'],
  [ErrorProductType.NAN_PRICE]: ['price'],
  [ErrorProductType.NAN_COEF]: ['coef'],
  [ErrorProductType.UNKNOWN_VAT]: ['vat'],
  [ErrorProductType.UNKNOWN_UNIT]: ['unit'],

  [ErrorProductType.PRICE_MORE_THAN_NETPRICE]: ['netPrice', 'price'],
  [ErrorProductType.COEF_NETPRICE_INCORRECT]: ['coef', 'netPrice'],

  [ErrorProductType.PROCESSING_ERROR]: [],
  [ErrorProductType.UNKNOWN_SYMBOL]: [],
  [ErrorProductType.NO_ID]: [],
  [ErrorProductType.NAN_ID]: [],
  [ErrorProductType.PRODUCT_NOT_FOUND]: [],
}

export const isErrorCorrected = (error: ErrorProduct, corrections: Partial<ProductCreation>) => {
  const properties = errorProperties[error.type]
  return (
    properties.some((property) => property && property in corrections) ||
    (error.type === ErrorProductType.UNKNOWN_SYMBOL && Object.hasOwn(corrections, error.property))
  )
}

export const sendProductsCreationByChunk = async ({
  productsToSend,
  companyId,
  onModify,
  onProgress,
}: {
  productsToSend: ProductCreation[]
  companyId: string
  onModify: boolean
  onProgress: ({ checked, processed }: { checked: boolean; processed: number }) => void
}): Promise<{
  success: boolean
  csv?: string
  products?: ProductCreation[]
  productsWithErrors?: ProductCreationWithErrors[]
  error?: ErrorUploadProduct
}> => {
  onProgress({ checked: false, processed: 0 })

  const checkProducts = await sendProductsRawByChunk({
    productsToSend: productsToSend as unknown as ProductRaw[],
    companyId,
    onModify,
  })

  if (!checkProducts.success || checkProducts.productsWithErrors?.length > 0) {
    console.log('Errors still exist in the products.')
    return {
      success: false,
      products: checkProducts.products,
      productsWithErrors: checkProducts.productsWithErrors,
      error: {
        type: 'PRODUCT_WITH_ERRORS',
        products: checkProducts.products,
        productsWithErrors: checkProducts.productsWithErrors,
      },
    }
  }
  if (!checkProducts.products || checkProducts.products?.length === 0) {
    return { success: false, error: { type: 'NO_PRODUCTS' } }
  }

  onProgress({ checked: true, processed: 0 })
  const products: ProductCreation[] = []
  const productsWithErrors: ProductCreationWithErrors[] = []
  const csvs: string[] = []
  let error: ErrorUploadProduct = null

  const CHUNK_SIZE = 50

  for (let i = 0; i < productsToSend.length; i += CHUNK_SIZE) {
    const chunk = productsToSend.slice(i, i + CHUNK_SIZE)

    const res = onModify
      ? await ApiArtizenBO.products.modifyProductsByCompany(companyId, chunk)
      : await ApiArtizenBO.products.importProductsByCompany(companyId, chunk)

    if (res.error) error = res.error

    if (res.products) products.push(...res.products)
    if (res.productsWithErrors) productsWithErrors.push(...res.productsWithErrors)
    if (res.success && res.csv) csvs.push(res.csv)

    onProgress?.({ checked: true, processed: Math.min(i + chunk.length, productsToSend.length) })
  }

  const finalCSV = csvs
    .map((csv) => csv.trim())
    .filter(Boolean)
    .map((csv, index) => {
      const lines = csv.split('\n')
      return index === 0 ? lines : lines.slice(1)
    })
    .flat()
    .join('\n')

  return { success: !error, products, productsWithErrors, csv: finalCSV, error }
}

export const sendProductsRawByChunk = async ({
  productsToSend,
  companyId,
  onModify,
}: {
  productsToSend: ProductRaw[]
  companyId: string
  onModify: boolean
}): Promise<{
  success: boolean
  products?: ProductCreation[]
  productsWithErrors?: ProductCreationWithErrors[]
  error?: string
}> => {
  const products: ProductCreation[] = []
  const productsWithErrors: ProductCreationWithErrors[] = []

  const CHUNK_SIZE = 200

  for (let i = 0; i < productsToSend.length; i += CHUNK_SIZE) {
    const chunk = productsToSend.slice(i, i + CHUNK_SIZE)

    const result = await ApiArtizenBO.products.previewWithProductsRaw(companyId, chunk, onModify)

    if (!result.success) {
      return result
    }

    if (result.products) products.push(...result.products)
    if (result.productsWithErrors) productsWithErrors.push(...result.productsWithErrors)
  }

  return { success: true, products, productsWithErrors }
}
